// Activity.jsx - Every event across every order, newest first, filterable by type.
//
// This is the page you open when something has gone wrong and you need to know
// what actually happened, so errors are visually loud and everything links back
// to its order.

import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAsync } from "../lib/hooks.js";
import { adminApi } from "../lib/api.js";
import { dayTime, ago, truncate } from "../lib/format.js";
import { Panel, Loading, Alert, Empty, Tag, Button } from "../ui/kit.jsx";
import { Flag, Note, Message, Phone, Truck, Camera, Dollar, IdCard, Refresh, Check } from "../ui/icons.jsx";

const TYPES = [
  { value: "", label: "Everything" },
  { value: "error", label: "Errors" },
  { value: "status", label: "Status changes" },
  { value: "offer,payout", label: "Money" },
  { value: "shipping", label: "Shipping" },
  { value: "message,call", label: "Contact" },
  { value: "note", label: "Notes" },
  { value: "system", label: "System" },
];

const ICON = {
  status: Flag, note: Note, message: Message, call: Phone, shipping: Truck,
  media: Camera, offer: Dollar, payout: Dollar, identity: IdCard, agreement: Check,
  system: Refresh, error: Flag,
};

export default function Activity() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const page = Number(params.get("page")) || 1;

  const { data, loading, error, reload } = useAsync(
    (signal) => adminApi.events({ type: type || undefined, page, limit: 80 }, signal),
    [type, page]
  );

  const setType = (next) => {
    const query = new URLSearchParams();
    if (next) query.set("type", next);
    setParams(query);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="display display-lg text-ink">Activity</h1>
          <p className="hint mt-1">Everything that has happened, across every order.</p>
        </div>
        <Button size="sm" variant="quiet" onClick={reload}><Refresh size={15} /> Refresh</Button>
      </header>

      <div className="chip-row mb-5">
        {TYPES.map((entry) => (
          <button
            key={entry.value}
            type="button"
            onClick={() => setType(entry.value)}
            className={clsx("btn btn-sm", type === entry.value ? "btn-ink" : "btn-quiet")}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {error && <Alert tone="error" className="mb-4">{error.message}</Alert>}

      <Panel>
        {loading ? (
          <Loading />
        ) : !data?.items?.length ? (
          <Empty title="Nothing logged in this view" icon={Note} />
        ) : (
          <ul className="divide-y divide-rule-soft">
            {data.items.map((event) => {
              const Glyph = ICON[event.type] || Note;
              const isError = event.type === "error";
              return (
                <li key={event._id} className={clsx("px-4 py-3 flex gap-3.5", isError && "bg-critical-wash/50")}>
                  <span className={clsx("mt-0.5 shrink-0", isError ? "text-critical" : "text-faint")}>
                    <Glyph size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2.5">
                      <p className={clsx("text-[14px] font-medium", isError ? "text-critical" : "text-ink")}>
                        {event.title}
                      </p>
                      {event.orderNumber && (
                        <Link to={`/admin/orders/${event.orderNumber}`} className="text-[12.5px] num text-muted hover:text-ink link">
                          {event.orderNumber}
                        </Link>
                      )}
                      {event.visibility === "customer" && <Tag tone="bronze">Customer sees this</Tag>}
                    </div>
                    {event.detail && (
                      <p className="hint mt-0.5 leading-snug">{truncate(event.detail, 260)}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[12px] text-faint whitespace-nowrap">{ago(event.at)}</p>
                    <p className="text-[11.5px] text-faint whitespace-nowrap hidden xs:block">{event.actor?.name || "System"}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="hint num">Page {data.page} of {data.pages}</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="quiet"
              disabled={page <= 1}
              onClick={() => setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("page", String(page - 1));
                return next;
              })}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="quiet"
              disabled={page >= data.pages}
              onClick={() => setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("page", String(page + 1));
                return next;
              })}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
